import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DateField } from "@/components/ui/date-field";
import { MoneyInput } from "@/components/ui/money-input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Trash2, CheckCircle, Pencil } from "lucide-react";
import { format } from "date-fns";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { toast } from "sonner";

type Payment = {
  data: string;
  bank_account_id: string;
  payment_method_id: string;
  valor: number;
  fee_amount?: number;
  net_amount?: number;
  descricao?: string;
  status: "confirmado" | "pendente";
  data_prevista?: string;
};

interface PaymentsListProps {
  payments: Payment[];
  onPaymentsChange: (payments: Payment[]) => void;
  valorTotal: number;
  valorPago: number;
  valorPendente: number;
  showSummary?: boolean;
  showDetails?: boolean;
  readOnly?: boolean;
}

export interface PaymentsListHandle {
  savePendingChanges: () => { success: boolean; payments: Payment[] };
}

type PaymentFormState = {
  optionValue: string;
  valor: number;
  netAmount: number;
  data: string;
  status: "confirmado" | "pendente";
  descricao: string;
  data_prevista: string;
};

const getToday = () => format(new Date(), "yyyy-MM-dd");

export const PaymentsList = forwardRef<PaymentsListHandle, PaymentsListProps>(function PaymentsList(
  {
    payments,
    onPaymentsChange,
    valorTotal,
    valorPago,
    valorPendente,
    showSummary = true,
    showDetails = true,
    readOnly = false,
  }: PaymentsListProps,
  ref,
) {
  const [newPaymentForm, setNewPaymentForm] = useState<PaymentFormState | null>(null);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editPaymentForm, setEditPaymentForm] = useState<PaymentFormState | null>(null);
  const lastInsertedMethod = useRef<string | null>(null);
  const paymentsRef = useRef<Payment[]>(payments);

  useEffect(() => {
    paymentsRef.current = payments;
    if (payments.length > 0) {
      const last = payments[payments.length - 1];
      if (last.bank_account_id && last.payment_method_id) {
        lastInsertedMethod.current = `${last.bank_account_id}-${last.payment_method_id}`;
      }
    }
  }, [payments]);

  // Buscar contas bancárias com métodos
  const { data: bankAccountsData } = useQuery({
    queryKey: ["bank-accounts-with-methods"],
    queryFn: async () => {
      const { data: accounts, error } = await supabase
        .from("bank_accounts")
        .select(`
          id,
          nome,
          bank_accounts_methods (
            payment_method_id,
            payment_methods:payment_method_id (
              id,
              nome,
              slug
            )
          )
        `)
        .eq("status", "ativo")
        .order("nome");
      
      if (error) throw error;
      return accounts;
    },
  });

  // Criar lista de opções "Conta - Método"
  const accountMethodOptions = useMemo(() => {
    if (!bankAccountsData) return [];
    
    const options: Array<{ value: string; label: string; bankAccountId: string; paymentMethodId: string; paymentMethodSlug?: string }> = [];
    
    bankAccountsData.forEach(account => {
      account.bank_accounts_methods?.forEach((bam: any) => {
        if (bam.payment_methods) {
          options.push({
            value: `${account.id}-${bam.payment_methods.id}`,
            label: `${account.nome} - ${bam.payment_methods.nome}`,
            bankAccountId: account.id,
            paymentMethodId: bam.payment_methods.id,
            paymentMethodSlug: bam.payment_methods.slug,
          });
        }
      });
    });
    
    return options;
  }, [bankAccountsData]);

  const findOptionByValue = (value: string | null) =>
    value ? accountMethodOptions.find((opt) => opt.value === value) : undefined;

  const ensureOptionValue = (value?: string | null) => {
    if (value && findOptionByValue(value)) {
      return value;
    }

    if (lastInsertedMethod.current && findOptionByValue(lastInsertedMethod.current)) {
      return lastInsertedMethod.current;
    }

    // Preferir PIX se disponível
    const pix = accountMethodOptions.find((opt) => opt.paymentMethodSlug?.toLowerCase() === "pix");
    if (pix) return pix.value;

    return accountMethodOptions[0]?.value ?? "";
  };

  const applyPaymentsChange = (updated: Payment[]) => {
    paymentsRef.current = updated;
    onPaymentsChange(updated);
  };

  const getPaymentLabel = (payment: Payment) => {
    const option = accountMethodOptions.find(
      (opt) => opt.bankAccountId === payment.bank_account_id && opt.paymentMethodId === payment.payment_method_id,
    );
    return option?.label || "Não especificado";
  };

  const isCardOrVoucherMethod = (paymentMethodSlug?: string | null) => {
    if (!paymentMethodSlug) return false;
    const slug = paymentMethodSlug.toLowerCase();
    return (
      slug.includes("credito") ||
      slug.includes("crédito") ||
      slug.includes("debito") ||
      slug.includes("débito") ||
      slug.includes("voucher")
    );
  };

  const getForecastDateLabel = (optionValue: string, status: "confirmado" | "pendente") => {
    if (status === "confirmado") {
      return "Liquidação";
    }
    if (status === "pendente") {
      return "Previsão Pag.";
    }
    return "Data de Previsão";
  };

  const getPaymentDateLabel = (status: "confirmado" | "pendente") => {
    if (status === "pendente") {
      return "Registro";
    }
    return "Data Pag.";
  };

  const removePayment = (index: number) => {
    if (editingIndex === index) {
      setEditingIndex(null);
      setEditPaymentForm(null);
    }
    applyPaymentsChange(payments.filter((_, i) => i !== index));
  };

  const confirmPayment = (index: number) => {
    const updatedPayments = [...payments];
    const nowIso = new Date().toISOString();
    updatedPayments[index] = {
      ...updatedPayments[index],
      status: "confirmado",
      data: getToday(),
      // timestamps auxiliares no JSON do pagamento
      // created_at já pode existir; confirmed_at marca o momento da confirmação
      ...(updatedPayments[index] as any),
    } as any;
    (updatedPayments[index] as any).confirmed_at = (updatedPayments[index] as any).confirmed_at || nowIso;
    applyPaymentsChange(updatedPayments);
  };

  const startEditingPayment = (index: number) => {
    const payment = payments[index];
    if (!payment) return;
    setEditingIndex(index);
    setEditPaymentForm({
      optionValue: `${payment.bank_account_id}-${payment.payment_method_id}`,
      valor: payment.valor,
      netAmount: payment.net_amount ?? payment.valor,
      data: payment.data || getToday(),
      status: payment.status,
      descricao: payment.descricao || "",
      data_prevista: payment.data_prevista || "",
    });
  };

  const handleEditFormChange = <K extends keyof PaymentFormState>(field: K, value: PaymentFormState[K]) => {
    setEditPaymentForm((current) => {
      if (!current) return current;
      const previousValor = current.valor;
      const previousNet = current.netAmount;
      const next: PaymentFormState = { ...current, [field]: value };
      if (field === "valor") {
        const newValor = value as number;
        const netFollowsValor = Math.abs(previousNet - previousValor) < 0.01 || previousNet <= 0;
        if (netFollowsValor) {
          next.netAmount = newValor;
        }
      }
      if (field === "status" && value === "confirmado") {
        next.data_prevista = "";
      }
      return next;
    });
  };

  const handleNewFormChange = <K extends keyof PaymentFormState>(field: K, value: PaymentFormState[K]) => {
    setNewPaymentForm((current) => {
      if (!current) return current;
      const previousValor = current.valor;
      const previousNet = current.netAmount;
      const next: PaymentFormState = { ...current, [field]: value };
      if (field === "valor") {
        const newValor = value as number;
        const netFollowsValor = Math.abs(previousNet - previousValor) < 0.01 || previousNet <= 0;
        if (netFollowsValor) {
          next.netAmount = newValor;
        }
      }
      if (field === "status" && value === "confirmado") {
        next.data_prevista = "";
      }
      return next;
    });
  };

  const saveEditPayment = (): boolean => {
    if (editingIndex === null || !editPaymentForm) {
      return true;
    }

    const option = findOptionByValue(editPaymentForm.optionValue);
    if (!option) {
      toast.error("Selecione uma conta/método válido.");
      return false;
    }

    if (editPaymentForm.valor <= 0) {
      toast.error("Informe um valor maior que zero para o pagamento.");
      return false;
    }

    if (editPaymentForm.netAmount <= 0) {
      toast.error("Informe um valor líquido maior que zero.");
      return false;
    }

    if (editPaymentForm.netAmount > editPaymentForm.valor) {
      toast.error("O valor líquido não pode ser maior que o valor bruto.");
      return false;
    }

    const updatedPayments = [...payments];
    const nowIso = new Date().toISOString();
    const netAmount = Number(editPaymentForm.netAmount.toFixed(2));
    const feeAmount = Number((editPaymentForm.valor - netAmount).toFixed(2));
    const nextPayment: any = {
      bank_account_id: option.bankAccountId,
      payment_method_id: option.paymentMethodId,
      valor: editPaymentForm.valor,
      net_amount: netAmount,
      fee_amount: feeAmount >= 0 ? feeAmount : 0,
      data: editPaymentForm.data || getToday(),
      status: editPaymentForm.status,
      descricao: editPaymentForm.descricao || "",
      data_prevista:
        editPaymentForm.status === "pendente" ? editPaymentForm.data_prevista || getToday() : "",
    };
    // manter timestamps anteriores, ou criar se necessário
    const prev = payments[editingIndex] as any;
    nextPayment.created_at = prev?.created_at || nowIso;
    if (editPaymentForm.status === "confirmado") {
      nextPayment.confirmed_at = prev?.confirmed_at || nowIso;
    } else {
      nextPayment.confirmed_at = prev?.confirmed_at || undefined;
    }

    updatedPayments[editingIndex] = nextPayment;

    applyPaymentsChange(updatedPayments);
    lastInsertedMethod.current = option.value;
    setEditingIndex(null);
    setEditPaymentForm(null);
    return true;
  };

  const saveNewPayment = (): boolean => {
    if (!newPaymentForm) {
      return true;
    }

    const effectiveOptionValue = ensureOptionValue(newPaymentForm.optionValue || lastInsertedMethod.current);
    const option = findOptionByValue(effectiveOptionValue);
    if (!option) {
      toast.error("Selecione uma conta e método para registrar o pagamento.");
      return false;
    }

    if (newPaymentForm.valor <= 0) {
      toast.error("Valor do pagamento deve ser maior que zero.");
      return false;
    }

    if (newPaymentForm.netAmount <= 0) {
      toast.error("Valor líquido deve ser maior que zero.");
      return false;
    }

    if (newPaymentForm.netAmount > newPaymentForm.valor) {
      toast.error("Valor líquido não pode ser maior que o valor informado.");
      return false;
    }

    const nowIso = new Date().toISOString();
    const netAmount = Number(newPaymentForm.netAmount.toFixed(2));
    const feeAmount = Number((newPaymentForm.valor - netAmount).toFixed(2));
    const payment: any = {
      bank_account_id: option.bankAccountId,
      payment_method_id: option.paymentMethodId,
      valor: newPaymentForm.valor,
      net_amount: netAmount,
      fee_amount: feeAmount >= 0 ? feeAmount : 0,
      data: newPaymentForm.data || getToday(),
      status: newPaymentForm.status,
      descricao: newPaymentForm.descricao || "",
      data_prevista:
        newPaymentForm.status === "pendente" ? newPaymentForm.data_prevista || getToday() : "",
    };
    payment.created_at = nowIso;
    if (newPaymentForm.status === "confirmado") {
      payment.confirmed_at = nowIso;
    }

    const updatedPayments = [...payments, payment];
    applyPaymentsChange(updatedPayments);
    lastInsertedMethod.current = option.value;

    const remainingAfterInsert = Math.max(valorPendente - payment.valor, 0);
    if (remainingAfterInsert > 0) {
      setNewPaymentForm({
        optionValue: option.value,
        valor: remainingAfterInsert,
        netAmount: remainingAfterInsert,
        data: getToday(),
        status: "confirmado",
        descricao: "",
        data_prevista: "",
      });
    } else {
      setNewPaymentForm(null);
    }

    return true;
  };

  const cancelEditing = () => {
    setEditingIndex(null);
    setEditPaymentForm(null);
  };

  useEffect(() => {
    // Se não houver opções de conta/método, não podemos registrar
    if (accountMethodOptions.length === 0) {
      setNewPaymentForm(null);
      return;
    }

    // Se a soma dos pagamentos (qualquer status) já cobrir o total, não mostrar formulário
    const totalRegistrado = payments.reduce((sum, p) => sum + (p?.valor || 0), 0);
    if (totalRegistrado >= valorTotal) {
      setNewPaymentForm(null);
      return;
    }

    // Se ainda há valor pendente (pela regra antiga), preparar formulário com esse valor
    if (valorPendente <= 0) {
      setNewPaymentForm(null);
      return;
    }

    setNewPaymentForm((current) => {
      const optionValue = ensureOptionValue(current?.optionValue);
      const status = current?.status ?? "confirmado";
      const previousNet = current?.netAmount ?? 0;
      const previousValor = current?.valor ?? 0;
      const netFollowsValor = !current || Math.abs(previousNet - previousValor) < 0.01 || previousNet <= 0;
      return {
        optionValue,
        valor: valorPendente,
        netAmount: netFollowsValor ? valorPendente : previousNet,
        data: current?.data ?? getToday(),
        status,
        descricao: current?.descricao ?? "",
        data_prevista: status === "pendente" ? current?.data_prevista ?? getToday() : "",
      };
    });
  }, [valorPendente, accountMethodOptions, payments, valorTotal]);

  const getStatusBadge = () => {
    const hasPending = payments.some((p) => p.status === "pendente");
    if (hasPending) return <span className="badge-soft-yellow">Parcial</span>;
    if (valorPago >= valorTotal) return <span className="badge-soft-green">Completo</span>;
    if (valorPago > 0) return <span className="badge-soft-yellow">Parcial</span>;
    return <span className="badge-soft-red">Pendente</span>;
  };

  const totalRegistradoGeral = payments.reduce((sum, p) => sum + (p?.valor || 0), 0);
  const valorLiquidoRecebido = payments.reduce((sum, p) => {
    if (p.status !== "confirmado") return sum;
    const net = typeof p.net_amount === "number" ? p.net_amount : p.valor;
    return sum + (net || 0);
  }, 0);
  const cobriuTotal = totalRegistradoGeral >= valorTotal;
  const canShowNewForm = !cobriuTotal && valorPendente > 0 && !!newPaymentForm && accountMethodOptions.length > 0;

  const statusSummary = valorPago >= valorTotal ? "complete" : valorPago > 0 ? "partial" : "pending";
  const newFormBorderClass =
    statusSummary === "pending"
      ? "border-2 border-red-500 ring-2 ring-red-500/40"
      : statusSummary === "partial"
      ? "border-2 border-yellow-400 ring-2 ring-yellow-400/40"
      : "";

  useImperativeHandle(ref, () => ({
    savePendingChanges: () => {
      if (!saveEditPayment()) {
        return { success: false, payments: paymentsRef.current };
      }

      if (!saveNewPayment()) {
        return { success: false, payments: paymentsRef.current };
      }

      return { success: true, payments: paymentsRef.current };
    },
  }));

  return (
    <div className="space-y-4">
      {showSummary && (
      <div className="bg-gray-200 dark:bg-muted px-4 py-2 rounded-lg space-y-1">
        <div className="flex justify-between items-center">
          <h3 className="font-semibold">Controle de Pagamentos:</h3>
          {getStatusBadge()}
        </div>
        <div className="text-md">
          <div className="flex flex-wrap items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground">Pago:</span>
              <span className="font-black text-success">
                {new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(valorPago)}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground">Pendente:</span>
              <span className="font-black text-red-500">
                {new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(valorPendente)}
              </span>
            </div>
            {valorLiquidoRecebido !== valorPago && (
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground">Recebido líquido:</span>
                <span className="font-black text-primary">
                  {new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(valorLiquidoRecebido)}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
      )}

      {showDetails && payments.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium">Pagamentos Registrados</h4>
          {payments.map((payment, index) => (
            <div key={index} className="bg-gray-200 dark:bg-muted px-4 pt-1 pb-2 rounded-lg">
              {(!readOnly && editingIndex === index && editPaymentForm) ? (
                <div className="space-y-0" onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}>
                  <div className="flex items-center justify-between">
                    <h4 className="font-semibold text-md">Editando Pagamento</h4>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={cancelEditing}
                      className="text-red-600 hover:text-white hover:bg-red-500 -mr-3"
                      title="Cancelar edição"
                    >
                      X
                    </Button>
                  </div>
                  <div className="space-y-3" style={{ width: '100%', boxSizing: 'border-box' }}>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                      <div className="space-y-1">
                        <label className="text-sm font-medium">Conta - Método 
                        <span className="text-red-500"> *</span></label>
                        <Select value={editPaymentForm.optionValue} onValueChange={(value) => handleEditFormChange("optionValue", value)}>
                          <SelectTrigger>
                            <SelectValue placeholder="Selecione conta e método" />
                          </SelectTrigger>
                          <SelectContent>
                            {accountMethodOptions.map((option) => (
                              <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-1">
                        <label className="text-sm font-medium">
                          Valor Recebido 
                          <span className="text-red-500"> *</span>
                        </label>
                        <MoneyInput value={editPaymentForm.valor} onValueChange={(value) => handleEditFormChange("valor", value)} placeholder="0,00" />
                      </div>

                      <div className="space-y-1">
                        <label className="text-sm font-medium">
                          {getPaymentDateLabel(editPaymentForm.status)}
                        </label>
                        <DateField
                          value={editPaymentForm.data}
                          onChange={(e) => handleEditFormChange("data", e.target.value)}
                          title={getPaymentDateLabel(editPaymentForm.status)}
                        />
                      </div>

                      <div className="space-y-1">
                        <label className="text-sm font-medium">Valor Líquido 
                        <span className="text-red-500"> *</span></label>
                        <MoneyInput value={editPaymentForm.netAmount} onValueChange={(value) => handleEditFormChange("netAmount", value)} placeholder="0,00" />
                      
                        <span className="text-xs text-muted-foreground">
                          Subtraindo Taxas
                        </span>
                      </div>

                      <div className="space-y-1">
                        <label className="text-sm font-medium">
                          {getForecastDateLabel(editPaymentForm.optionValue, editPaymentForm.status)}
                        </label>
                        <DateField
                          value={editPaymentForm.data_prevista || ""}
                          onChange={(e) => handleEditFormChange("data_prevista", e.target.value)}
                          title={getForecastDateLabel(editPaymentForm.optionValue, editPaymentForm.status)}
                        />
                      </div>
                    </div>

                    <div>
                      <div className="grid items-start gap-2 w-full grid-cols-[1fr_170px_auto] -mt-3">
                        <div className="space-y-1">
                          <label className="text-sm font-medium">Descrição</label>
                          <Input
                            className="w-full"
                            value={editPaymentForm.descricao}
                            onChange={(e) => handleEditFormChange("descricao", e.target.value)}
                            placeholder="Ex: Sinal, Entrada, etc."
                          />
                        </div>
                        <div className="space-y-1 w-[170px]">
                          <label className="text-sm font-medium">Status</label>
                          <Select value={editPaymentForm.status} onValueChange={(value: "confirmado" | "pendente") => handleEditFormChange("status", value)}>
                            <SelectTrigger className="w-full">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="confirmado">Confirmado</SelectItem>
                              <SelectItem value="pendente">Pendente</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-1 flex flex-col justify-end">
                          <label className="text-sm mb-1 font-medium invisible">Ações</label>
                          <div className="flex items-end gap-2 whitespace-nowrap">
                            <Button
                              type="button"
                              variant="ghost"
                              onClick={cancelEditing}
                              className="text-red-600 hover:text-white hover:bg-red-500"
                            >
                              Cancelar
                            </Button>
                            <Button type="button" onClick={() => saveEditPayment()}>Salvar</Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-[1fr_auto_auto_auto] items-center gap-3">
                  <div className="space-y-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{getPaymentLabel(payment)}</span>
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {format(new Date(payment.data), "dd/MM/yyyy")}
                      {payment.data_prevista && format(new Date(payment.data), "dd/MM/yyyy") !== format(new Date(payment.data_prevista), "dd/MM/yyyy") && ` • ${payment.status === "confirmado" ? "Liquidado" : "Previsto"}: ${format(new Date(payment.data_prevista), "dd/MM/yyyy")}`}
                      {payment.descricao && ` • ${payment.descricao}`}
                    </div>
                  </div>
                  <div className="flex justify-end min-w-[100px]">
                    {payment.status === "pendente" ? (
                      <span className="badge-soft-yellow">pendente</span>
                    ) : (
                      <span className="badge-soft-green">confirmado</span>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-1 whitespace-nowrap min-w-[140px]">
                    <span className="font-semibold">{new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(payment.valor)}</span>
                    {typeof payment.net_amount === "number" && Math.abs(payment.net_amount - payment.valor) > 0.009 && (
                      <span className="text-xs font-bold text-blue-600 dark:text-blue-400">
                        Líquido: {new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(payment.net_amount)}
                      </span>
                    )}
                  </div>
                  {!readOnly ? (
                    <div className="flex items-center gap-1 min-w-[128px] justify-end">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => startEditingPayment(index)}
                        title="Editar pagamento"
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => removePayment(index)}
                        className="text-red-600 hover:bg-red-500 hover:text-white"
                        title="Remover pagamento"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                      {payment.status === "pendente" && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => confirmPayment(index)}
                          className="text-green-600 hover:bg-green-500 hover:text-white"
                          title="Confirmar pagamento"
                        >
                          <CheckCircle className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  ) : (
                    <div className="min-w-[128px]"></div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {showDetails ? null : null}

      {showDetails && valorPendente > 0 && accountMethodOptions.length === 0 && (
        <div className="border border-dashed rounded-lg p-4 text-sm text-muted-foreground">
          Cadastre uma conta e método de pagamento para registrar pagamentos.
        </div>
      )}

      {showDetails && !readOnly && canShowNewForm && newPaymentForm && (
        <div className={`border rounded-lg p-4 space-y-3 ${newFormBorderClass}`} onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}>
          <h4 className="font-semibold text-sm">Registrar Pagamento Realizado ou Planejado</h4>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
            <div className="space-y-1 col-span-2 md:col-span-2">
              <label className="text-sm font-medium">Conta - Método 
              <span className="text-red-500"> *</span></label>
              <Select
                value={newPaymentForm.optionValue}
                onValueChange={(value) => handleNewFormChange("optionValue", value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione conta e método" />
                </SelectTrigger>
                <SelectContent>
                  {accountMethodOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1 col-span-1 md:col-span-1">
              <label className="text-sm font-medium">
                Valor Recebido
                <span className="text-red-500">*</span>
              </label>
              <MoneyInput
                value={newPaymentForm.valor}
                onValueChange={(value) => handleNewFormChange("valor", value)}
                placeholder="0,00"
              />
            </div>

            <div className="space-y-1 col-span-1">
              <label className="text-sm font-medium">
                {getPaymentDateLabel(newPaymentForm.status)}
              </label>
              <DateField
                value={newPaymentForm.data}
                onChange={(e) => handleNewFormChange("data", e.target.value)}
                title={getPaymentDateLabel(newPaymentForm.status)}
              />
            </div>

            <div className="space-y-1 col-span-1 md:col-span-1">
              <label className="text-sm font-medium">
                Valor Líquido
                <span className="text-red-500">*</span>
              </label>
              <MoneyInput
                value={newPaymentForm.netAmount}
                onValueChange={(value) => handleNewFormChange("netAmount", value)}
                placeholder="0,00"
              />
              <span className="text-xs text-muted-foreground">
                Subtraindo Taxas
              </span>
            </div>

            <div className="space-y-1 col-span-1">
              <label className="text-sm font-medium">
                {getForecastDateLabel(newPaymentForm.optionValue, newPaymentForm.status)}
              </label>
              <DateField
                value={newPaymentForm.data_prevista}
                onChange={(e) => handleNewFormChange("data_prevista", e.target.value)}
                title={getForecastDateLabel(newPaymentForm.optionValue, newPaymentForm.status)}
              />
            </div>

            <div className="col-span-2 md:col-span-6 -mt-3">
              <div className="grid items-start gap-2 w-full grid-cols-[1fr_170px_auto]">
                <div className="space-y-1">
                  <label className="text-sm font-medium">Descrição</label>
                  <Input
                    className="w-full"
                    value={newPaymentForm.descricao}
                    onChange={(e) => handleNewFormChange("descricao", e.target.value)}
                    placeholder="Ex: Sinal, Entrada, etc."
                  />
                </div>
                <div className="space-y-1 w-[170px]">
                  <label className="text-sm font-medium">Status</label>
                  <Select
                    value={newPaymentForm.status}
                    onValueChange={(value: "confirmado" | "pendente") => handleNewFormChange("status", value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="confirmado">Confirmado</SelectItem>
                      <SelectItem value="pendente">Pendente</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1 flex flex-col justify-end">
                  <label className="text-sm mb-1 font-medium invisible">Ações</label>
                  <div className="flex items-center gap-2 justify-end">
                    <Button type="button" onClick={() => saveNewPayment()}>Inserir</Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
    </div>
  );
});
